import { useCallback, useEffect, useState } from "react";

export type ThemeChoice = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "shotflux:theme";

/** Resolve a choice to a concrete theme, consulting the OS for "system". */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  if (choice === "system") {
    const mql =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;
    return mql?.matches ? "dark" : "light";
  }
  return choice;
}

/** Apply a resolved theme by toggling the html[data-theme] attribute. */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  if (resolved === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

function readStoredChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* private mode / disabled storage */
  }
  return "system";
}

/**
 * Theme state for the app. Owns the `light | dark | system` choice, persists
 * it to localStorage, resolves `system` against the OS, and reflects the
 * result onto `html[data-theme]`. The no-FOUC bootstrap in index.html mirrors
 * this logic to avoid a flash before React mounts — keep the storage key and
 * resolution rule in sync.
 */
export function useTheme() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readStoredChoice());

  // Apply on mount + whenever the choice changes.
  useEffect(() => {
    applyTheme(resolveTheme(choice));
  }, [choice]);

  // While in "system", track OS changes live.
  useEffect(() => {
    if (choice !== "system" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(resolveTheme("system"));
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [choice]);

  const setTheme = useCallback((next: ThemeChoice) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setChoice(next);
  }, []);

  return { choice, resolved: resolveTheme(choice), setTheme };
}
